import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Volume2, Shield, Moon, Sun, Monitor, Save, ChevronRight, Settings, Camera, Loader2, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../config/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function StudentSettings() {
    const navigate = useNavigate();
    const { user, updateProfile } = useAuth(); // Destructure updateProfile from context
    const [theme, setTheme] = useState("system"); // system, light, dark
    const [notifications, setNotifications] = useState({
        communityReplies: true,
        adminMessages: true,
        dailySummary: false,
        soundEnabled: true
    });

    // Profile State
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [photoURL, setPhotoURL] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    // Load settings from Firebase/Local Storage
    useEffect(() => {
        const loadSettings = async () => {
            if (!user) return;
            try {
                // Initialize profile state from user context
                setFirstName(user.firstName || "");
                setLastName(user.lastName || "");
                setPhotoURL(user.photoURL || "");

                // Try to get from Firestore user preferences
                const prefRef = doc(db, "user_preferences", user.uid || user.id || user.email);
                const docSnap = await getDoc(prefRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setNotifications({ ...notifications, ...data.notifications });
                    setTheme(data.theme || "system");
                    // Prioritize Firestore photoURL if available (bypass Backend API limit)
                    if (data.photoURL) {
                        setPhotoURL(data.photoURL);
                    }
                }
            } catch (error) {
                console.error("Error loading settings:", error);
            } finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, [user]);

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert("File size too large (Max 5MB)");
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let newPhotoURL = photoURL;

            // 1. Upload Image to Firebase Storage if exists
            if (imageFile) {
                const storageRef = ref(storage, `profile_images/${user.id || user.uid}_${Date.now()}`);
                await uploadBytes(storageRef, imageFile);
                newPhotoURL = await getDownloadURL(storageRef);
            }

            // 2. Update Backend via AuthContext (API) - Best effort
            if (updateProfile) {
                try {
                    await updateProfile({
                        firstName,
                        lastName,
                        photoURL: newPhotoURL
                    });
                } catch (err) {
                    console.warn("Backend update failed, falling back to Firestore", err);
                }
            }

            // 3. Update Firestore 'users' collection (for Chat sync)
            // AND 'user_preferences' (for local persistence)
            const userId = user.uid || user.id || user.email;

            // Save to preferences (Theme, Notifications, AND PHOTO)
            const prefRef = doc(db, "user_preferences", userId);
            await setDoc(prefRef, {
                notifications,
                theme,
                photoURL: newPhotoURL, // Persist photo here
                firstName, // Persist name here too as backup
                lastName,
                updated_at: new Date()
            }, { merge: true });

            // Force update local state immediately
            setPhotoURL(newPhotoURL);

            alert("Settings saved successfully!");
            // Clear file input
            setImageFile(null);
            setImagePreview(null);

        } catch (error) {
            console.error("Error saving settings:", error);
            alert("Failed to save settings. " + (error.message || ""));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-3xl mx-auto px-4 py-4">
                    <button
                        onClick={() => navigate('/community')}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium text-sm mb-3 transition-colors"
                    >
                        <ArrowLeft size={18} />
                        <span>Back to Community</span>
                    </button>

                    <div className="flex items-center gap-3">
                        <div className="bg-gray-100 p-2.5 rounded-lg">
                            <Settings size={22} className="text-gray-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Settings</h1>
                            <p className="text-xs text-gray-500">Manage your preferences</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

                    {/* Notification Settings */}
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                            <Bell size={20} className="text-blue-500" />
                            Notifications
                        </h2>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-700">Community Replies</p>
                                    <p className="text-xs text-gray-500">Get notified when someone replies to you</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={notifications.communityReplies}
                                        onChange={e => setNotifications({ ...notifications, communityReplies: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-700">Admin Messages</p>
                                    <p className="text-xs text-gray-500">Always stay updated with admin announcements</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer opacity-60">
                                    <input
                                        type="checkbox"
                                        checked={notifications.adminMessages}
                                        disabled
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-blue-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5"></div>
                                </label>
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-700">Daily Summary</p>
                                    <p className="text-xs text-gray-500">Receive a daily digest of activity</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={notifications.dailySummary}
                                        onChange={e => setNotifications({ ...notifications, dailySummary: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Sound Settings */}
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                            <Volume2 size={20} className="text-purple-500" />
                            Sound & Appearance
                        </h2>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-700">Sound Effects</p>
                                    <p className="text-xs text-gray-500">Play sound when sending/receiving messages</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={notifications.soundEnabled}
                                        onChange={e => setNotifications({ ...notifications, soundEnabled: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>

                            {/* Theme Selection - Visual Only for now */}
                            <div>
                                <p className="font-medium text-gray-700 mb-3">Theme Preference</p>
                                <div className="grid grid-cols-3 gap-3">
                                    <button
                                        onClick={() => setTheme('light')}
                                        className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${theme === 'light' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`}
                                    >
                                        <Sun size={20} />
                                        <span className="text-xs font-medium">Light</span>
                                    </button>
                                    <button
                                        onClick={() => setTheme('dark')}
                                        className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${theme === 'dark' ? 'border-purple-500 bg-gray-800 text-white' : 'border-gray-200 hover:border-gray-300 bg-gray-900 text-gray-400'}`}
                                    >
                                        <Moon size={20} />
                                        <span className="text-xs font-medium">Dark Mode</span>
                                    </button>
                                    <button
                                        onClick={() => setTheme('system')}
                                        className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${theme === 'system' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`}
                                    >
                                        <Monitor size={20} />
                                        <span className="text-xs font-medium">System</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Verified Status (Read Only) */}
                    {/* Profile Settings (Editable) */}
                    <div className="p-6 bg-gray-50">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                            <User size={20} className="text-emerald-500" />
                            Profile Settings
                        </h2>

                        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 bg-white p-6 rounded-lg border border-gray-200">
                            {/* Avatar Upload */}
                            <div className="relative group">
                                <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-3xl overflow-hidden border-4 border-white shadow-sm">
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                    ) : photoURL ? (
                                        <img src={photoURL} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        (firstName?.[0] || 'U').toUpperCase()
                                    )}
                                </div>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600"
                                    title="Change Profile Picture"
                                >
                                    <Camera size={16} />
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>

                            {/* Info Fields */}
                            <div className="flex-1 w-full space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                                        <input
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
                                            placeholder="First Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
                                            placeholder="Last Name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Email Address</label>
                                    <input
                                        type="email"
                                        value={user?.email || ""}
                                        disabled
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                                    />
                                </div>

                                <div className="flex items-center gap-2 pt-2">
                                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${user?.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {user?.role === 'admin' ? 'ADMINISTRATOR' : 'STUDENT ACCOUNT'}
                                    </span>
                                    {user?.role === 'admin' && (
                                        <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                            <Shield size={12} /> VERIFIED
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={18} className="animate-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} /> Save Changes
                            </>
                        )}
                    </button>
                </div>
            </main>
        </div>
    );
}
